/**
 * Chart.js theming — the one place a canvas learns the design tokens.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * A canvas cannot read a Tailwind class, so every chart in the old dashboard
 * carried its own '#1f2937' / '#3b82f6' literals. Nine charts drifted apart, none
 * of them followed the theme, and dark mode painted grey text on a grey plot.
 * Colour here is read out of the same CSS custom properties the rest of the UI
 * uses — at draw time, not at import time — so a theme flip moves the charts
 * along with everything else.
 *
 * THE RULES THIS FILE ENFORCES
 * ----------------------------
 * - Legends are DOM, never canvas. Canvas text cannot be selected, searched, or
 *   read by a screen reader; `ChartFrame`'s `legend` prop is the real legend.
 * - A count axis is whole numbers. "2.5 calls" is not a quantity.
 * - A rate axis is FIXED 0..100. Auto-scaling turns 88% → 86% into a cliff, and
 *   makes two rate charts on the same screen silently non-comparable.
 * - Every axis states its unit.
 * - A gap in the data stays a gap: `spanGaps` is off, because a line drawn
 *   across a missing bucket invents a value the server never sent.
 * - Motion is 200 ms, and none at all when the reader asked for none.
 *
 * `applyChartTheme()` is idempotent and re-callable: the axis and tooltip
 * helpers resolve tokens on every call, so a re-render after a theme flip is
 * already correct, and re-calling this refreshes the globals too.
 */

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
} from 'chart.js';

/* Mirrors tailwind.config.js `fontFamily`. Canvas needs the literal stack. */
const FONT_SANS = "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif";
const FONT_MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

const BASE_SIZE = 12; // the type floor, in canvas as much as in the DOM
const MOTION_MS = 200;
const HIT_RADIUS = 12; // an invisible point still has to be hittable by a mouse

/** What an unresolvable token renders as: grey, i.e. visibly "unknown". */
const UNKNOWN = 'rgb(102, 112, 133)';

/* ─── Reading tokens off the document ────────────────────────────────────── */

let cachedRoot = null;
let cachedStyle = null;

/**
 * `getComputedStyle` hands back a LIVE declaration, so caching it is safe: when
 * the `.dark` class lands on <html> the same object starts reporting the dark
 * values. Caching only avoids re-acquiring it on every tick label.
 */
const rootStyle = () => {
  if (typeof document === 'undefined' || !document.documentElement) return null;
  const root = document.documentElement;
  if (root !== cachedRoot) {
    cachedRoot = root;
    cachedStyle = window.getComputedStyle(root);
  }
  return cachedStyle;
};

/** index.css stores colours as "R G B" so Tailwind can inject an alpha. */
const RGB_TRIPLE = /^(-?[\d.]+)[\s,]+(-?[\d.]+)[\s,]+(-?[\d.]+)$/;

/**
 * Resolve a CSS custom property to a colour a canvas can use.
 *   readToken('--ink-500')        -> 'rgb(102, 112, 133)'
 *   readToken('--brand', 0.15)    -> 'rgba(37, 99, 235, 0.15)'
 *
 * @param {string} name   custom property name, including the leading `--`.
 * @param {number} [alpha] 0..1. Canvas cannot resolve `var()`, so a translucent
 *   fill has to be built from the real channel values.
 * @returns {string} an rgb()/rgba() string, or `UNKNOWN` when the token is not
 *   defined — never `undefined`, which Chart.js would paint as opaque black.
 */
export function readToken(name, alpha) {
  const style = rootStyle();
  const raw = style ? String(style.getPropertyValue(name) || '').trim() : '';
  if (!raw) return UNKNOWN;

  const m = RGB_TRIPLE.exec(raw);
  if (!m) return raw; // already a complete colour (a hex or rgb() literal)

  const [, r, g, b] = m;
  return Number.isFinite(alpha) && alpha < 1
    ? `rgba(${r}, ${g}, ${b}, ${alpha})`
    : `rgb(${r}, ${g}, ${b})`;
}

/* ─── The series palette ─────────────────────────────────────────────────── */

/**
 * `neutral` and `slate` deliberately resolve to the same grey: neutral sentiment
 * and the outbound direction are both "no verdict", and the two names exist so a
 * chart declares which meaning it intends rather than reaching for a hue.
 */
const SERIES_TOKENS = {
  cat1: '--cat-1',
  cat2: '--cat-2',
  cat3: '--cat-3',
  good: '--good-fill',
  bad: '--bad-fill',
  neutral: '--slate-fill',
  brand: '--brand',
  slate: '--slate-fill',
  slateLight: '--slate-light',
};

const SEQ_TOKENS = ['--seq-0', '--seq-1', '--seq-2', '--seq-3', '--seq-4', '--seq-5', '--seq-6'];
const RANK_TOKENS = ['--rank-1', '--rank-2', '--rank-3', '--rank-4', '--rank-5'];

/**
 * Chart series colours. Every property is a getter, so `SERIES.cat1` is resolved
 * against the live theme at the moment it is read — a frozen snapshot taken at
 * import time would be wrong in dark mode and wrong again on every flip.
 *
 * Three categorical slots is the whole budget (see tailwind.config.js): past
 * three series, fold into "Other" or facet. `seq` is for continuous magnitude,
 * `rank` for ordinal position.
 */
export const SERIES = (() => {
  const s = {};
  Object.keys(SERIES_TOKENS).forEach((key) => {
    Object.defineProperty(s, key, {
      enumerable: true,
      get: () => readToken(SERIES_TOKENS[key]),
    });
  });
  Object.defineProperty(s, 'seq', {
    enumerable: true,
    get: () => SEQ_TOKENS.map((t) => readToken(t)),
  });
  Object.defineProperty(s, 'rank', {
    enumerable: true,
    get: () => RANK_TOKENS.map((t) => readToken(t)),
  });
  return Object.freeze(s);
})();

/* ─── Axes ───────────────────────────────────────────────────────────────── */
/* Chart.js 3.9 spells the axis line `grid.drawBorder` / `grid.borderColor`; 4.x
   spells it `border.*`. Both are set so an upgrade cannot silently drop it. */

const axisTitle = (text) => ({
  display: Boolean(text),
  text: text || '',
  color: readToken('--ink-500'),
  font: { family: FONT_SANS, size: BASE_SIZE, weight: '500' },
  padding: 4,
});

const gridLines = () => ({
  color: readToken('--line'),
  drawBorder: false,
  drawTicks: false,
});

const numericTicks = () => ({
  color: readToken('--ink-500'),
  font: { family: FONT_MONO, size: BASE_SIZE },
  padding: 8,
  maxTicksLimit: 5,
});

/**
 * A y-axis for absolute counts.
 * @param {string} title the unit — "Calls", "Follow-ups". Always say it.
 */
export function countAxis(title = 'Calls') {
  return {
    // A count axis that starts at 47 exaggerates every wobble into a trend.
    beginAtZero: true,
    title: axisTitle(title),
    grid: gridLines(),
    border: { display: false },
    ticks: { ...numericTicks(), precision: 0 },
  };
}

/**
 * A y-axis for a rate. Datasets feeding this are in percentage POINTS (0..100),
 * not ratios — divide-by-100 belongs in the chart, not here.
 */
export function rateAxis(title = '% of calls') {
  return {
    // Fixed, always. This is the whole point of the helper.
    min: 0,
    max: 100,
    title: axisTitle(title),
    grid: gridLines(),
    border: { display: false },
    ticks: {
      ...numericTicks(),
      stepSize: 25, // 0 / 25 / 50 / 75 / 100 — the same five lines on every chart
      precision: 0,
      callback: (value) => `${value}%`,
    },
  };
}

/**
 * The bucket (x) axis. A category scale, not a time scale: buckets arrive with
 * labels already formatted in the tenant's timezone by lib/time.js, which is the
 * only place that knows the timezone — and it saves shipping a date adapter.
 */
export function timeAxis(title = 'Date') {
  return {
    title: axisTitle(title),
    // Vertical gridlines on 30 daily buckets are noise; the baseline is the only
    // line worth drawing, so the grid is off but its border stays on.
    grid: {
      display: false,
      drawBorder: true,
      drawTicks: false,
      borderColor: readToken('--line'),
    },
    border: { display: true, color: readToken('--line') },
    ticks: {
      color: readToken('--ink-500'),
      font: { family: FONT_SANS, size: BASE_SIZE },
      padding: 6,
      autoSkip: true,
      maxTicksLimit: 8,
      // Rotated date labels are the first thing to become unreadable, and the
      // second thing to blow up the plot height.
      maxRotation: 0,
      minRotation: 0,
    },
  };
}

/* ─── Tooltip ────────────────────────────────────────────────────────────── */

/**
 * Inverted surface: `--ink-900` on `--surface` in light mode, and the same two
 * tokens swap roles in dark mode, so one recipe reads correctly in both.
 */
const tooltipTheme = () => ({
  backgroundColor: readToken('--ink-900'),
  titleColor: readToken('--surface'),
  bodyColor: readToken('--surface'),
  footerColor: readToken('--surface'),
  borderColor: readToken('--line-strong'),
  borderWidth: 1,
  cornerRadius: 6,
  padding: 10,
  caretSize: 5,
  caretPadding: 8,
  titleMarginBottom: 6,
  titleFont: { family: FONT_SANS, size: BASE_SIZE, weight: '600' },
  // Figures in a tooltip sit in a column, so they get the mono face.
  bodyFont: { family: FONT_MONO, size: BASE_SIZE },
  footerFont: { family: FONT_SANS, size: BASE_SIZE, weight: '400' },
  displayColors: true,
  usePointStyle: true,
  boxWidth: 8,
  boxHeight: 8,
  boxPadding: 6,
});

/**
 * A themed `plugins.tooltip` block.
 *
 * @param {object} [opts] anything Chart.js accepts, merged over the theme —
 *   typically `{ callbacks: { label, title, footer } }`. A partial `callbacks`
 *   object is fine: Chart.js resolves the missing keys from its own defaults.
 */
export function tooltip(opts = {}) {
  return {
    ...tooltipTheme(),
    // Hovering anywhere in a bucket's column should show that bucket, not
    // require hitting a 2px line.
    mode: 'index',
    intersect: false,
    ...opts,
  };
}

/* ─── Motion ─────────────────────────────────────────────────────────────── */

/** True when the reader has asked the OS for reduced motion. */
export function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    // Safari < 14 throws on an unsupported query rather than returning false.
    return false;
  }
}

/* ─── Global defaults ────────────────────────────────────────────────────── */

/** Merge into a `ChartJS.defaults` sub-object that may not exist yet. */
const mergeDefaults = (parent, key, values) => {
  parent[key] = Object.assign(parent[key] || {}, values);
};

/**
 * Register the Chart.js pieces this app actually draws with, and apply the
 * design tokens to `ChartJS.defaults`. Called once from src/index.js, and again
 * by the theme provider after flipping `.dark`.
 *
 * Registration lives here so no screen has to think about it. Only what is used
 * is registered — no radial scales, no pie/doughnut controllers, no Legend (DOM
 * legends), no Title plugin (axis titles are drawn by the scale itself).
 * `BarController`/`LineController` are included because the mixed bar+line
 * charts go through react-chartjs-2's generic `<Chart>`, which — unlike `<Line>`
 * and `<Bar>` — registers nothing on its own.
 */
export function applyChartTheme() {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarController,
    LineController,
    BarElement,
    LineElement,
    PointElement,
    Filler,
    Tooltip
  );

  const d = ChartJS.defaults;

  d.font.family = FONT_SANS;
  d.font.size = BASE_SIZE;
  // `color` routes to tick labels and axis titles; `borderColor` to gridlines
  // and axis lines. Setting the two roots themes every scale we never touch.
  d.color = readToken('--ink-500');
  d.borderColor = readToken('--line');

  // The container owns the height — ChartFrame's `height` prop, not an aspect
  // ratio Chart.js guessed from the canvas.
  d.responsive = true;
  d.maintainAspectRatio = false;

  // Legends are DOM. The Legend plugin is not registered, so in a tree-shaken
  // build its options block does not even exist yet — hence the merge rather
  // than a plain assignment. Setting it anyway keeps the rule true if some
  // future screen registers Legend.
  mergeDefaults(d.plugins, 'legend', { display: false });
  mergeDefaults(d.plugins, 'tooltip', tooltipTheme());

  d.interaction.mode = 'index';
  d.interaction.intersect = false;

  // Invisible points: a marker per daily bucket is clutter, but the value still
  // has to be reachable, hence the generous hit radius.
  d.elements.point.radius = 0;
  d.elements.point.hitRadius = HIT_RADIUS;
  d.elements.point.hoverRadius = 4;
  d.elements.point.hoverBorderWidth = 2;

  d.elements.line.borderWidth = 2;
  d.elements.line.tension = 0.25;
  d.elements.line.borderCapStyle = 'round';

  d.elements.bar.borderWidth = 0;

  // A missing bucket must read as missing, not be bridged into a plausible line.
  d.datasets.line.spanGaps = false;

  const reduced = prefersReducedMotion();
  // `animation: false` is the flag Chart.js checks to skip animation resolution
  // entirely, so it has to be the literal false — not a zero duration.
  d.animation = reduced ? false : { duration: MOTION_MS, easing: 'easeOutQuart' };
  // Hover restyling gets the same budget; stock is 400 ms.
  d.transitions.active.animation.duration = reduced ? 0 : MOTION_MS;
}
